import type { DateRangePickerProps } from '@risk-smart/themed-cloudscape-components/date-range-picker';
import dayjs from 'dayjs';

export type DateThresholdMode = 'onOrAfter' | 'onOrBefore';

/**
 * An inclusive one-sided date filter (CUS-217).
 *
 * Carried on the SAME `=` operator as the range value rather than as its own
 * `>=` / `<=` operators. Cloudscape auto-inserts the operator only when a
 * property has exactly one (`property-filter/internal.js` — "Insert operator
 * automatically if only one operator is defined"), so extra operators would cost
 * users a click and hide the range picker behind selecting "Equals".
 *
 * `type` discriminates against Cloudscape's own range values (`'absolute'` /
 * `'relative'`), so an already-saved range filter stays valid.
 */
export type DateThresholdValue = {
  type: 'threshold';
  mode: DateThresholdMode;
  /** `YYYY-MM-DD`. Naive local date — see the day-precision note below. */
  date: string;
};

export type DateFilterValue = DateRangePickerProps.Value | DateThresholdValue;

export const isDateThresholdValue = (
  value: unknown
): value is DateThresholdValue =>
  typeof value === 'object' &&
  value !== null &&
  (value as { type?: unknown }).type === 'threshold' &&
  typeof (value as { date?: unknown }).date === 'string';

/**
 * Day precision, so "on or before the 15th" includes all of the 15th. Both sides
 * are naive local dates, so the result does not depend on the viewer's timezone.
 */
export const matchDateThreshold = (
  itemValue: unknown,
  { mode, date }: DateThresholdValue
): boolean => {
  if (!itemValue || !date) {
    return false;
  }

  const item = dayjs(itemValue as string);
  const token = dayjs(date);

  if (!item.isValid() || !token.isValid()) {
    return false;
  }

  return mode === 'onOrAfter'
    ? item.isSame(token, 'day') || item.isAfter(token, 'day')
    : item.isSame(token, 'day') || item.isBefore(token, 'day');
};
